<template>
    <div>
        <q-dialog v-model="showHistoricResponses" @hide="showHistoricResponses = false">
            <FormHistoryResponse :client="selectedClient" formType="Te Iti Kahurangi" />
        </q-dialog>

        <DashboardClientSummary
            :total-sessions="totalSessions"
            :days-since-last-report="daysSinceLastReport"
            @view-history="onViewHistory"
        />

        <div class="main-content">
            <div class="grid-item spider-graph" :class="{ empty: !selectedClient }">
                <div class="grid-title">
                    SPIDER GRAPH
                    <q-icon class="q-mb-xs" size="16px" name="question_mark" color="primary" />
                    <q-tooltip class="text-body2 text-black bg-white" anchor="center right" self="center left" :offset="[10, 10]">
                        This spider graph shows average ratings for each wellbeing area across your form responses.
                    </q-tooltip>
                </div>

                <div v-if="!selectedClient">Select a client to view spider graph</div>
                <div v-else-if="isTempClient" class="tik-not-available">Not available for Te Kohinga People.</div>
                <div v-else-if="overallData">
                    <Radar :chartData="overallData" :chartOptions="radarOptions" />
                    <div class="chart-key q-mt-md">
                        <div class="key-item">
                            <div class="key-color" style="background-color: rgba(255, 193, 7, 0.2); border: 2px solid rgb(255, 193, 7);"></div>
                            <span class="key-label">Initial Form</span>
                        </div>
                        <div class="key-item">
                            <div class="key-color" style="background-color: rgba(2, 123, 227, 0.2); border: 2px solid rgb(2, 123, 227);"></div>
                            <span class="key-label">Final Form</span>
                        </div>
                    </div>
                </div>
                <div v-else class="text-center">
                    <q-spinner color="primary" size="50px" class="q-mt-md" />
                </div>
            </div>

            <div class="grid-item progress-grid" :class="{ empty: !selectedClient }">
                <div class="grid-title">
                    RECENT PROGRESS
                    <q-icon class="q-mb-xs" size="16px" name="question_mark" color="primary" />
                    <q-tooltip class="text-body2 text-black bg-white" anchor="bottom middle" self="top middle" :offset="[10, 10]">
                        The boxes below show the difference between the most recent and previous session for each wellbeing area.
                    </q-tooltip>
                </div>

                <div v-if="!selectedClient">Select a client to view progress</div>
                <div v-else-if="isTempClient" class="tik-not-available">Not available for Te Kohinga People.</div>
                <div v-else-if="overallData" class="last-session-stats">
                    <div v-for="stat in sessionStats" :key="stat.id" class="last-session-stats-stat">
                        <h3 :style="{ color: stat.value > 0 ? 'green' : stat.value < 0 ? 'plum' : 'inherit' }">
                            {{ stat.value > 0 ? '+' : '' }}{{ stat.value.toFixed(1) }}
                        </h3>
                        <span class="text-h6">{{ stat.statCategory }}</span>
                    </div>
                </div>
                <div v-else class="text-center">
                    <q-spinner color="primary" size="40px" class="q-mt-md" />
                </div>
            </div>

            <div class="grid-item narrative-bullets" :class="{ empty: !selectedClient }">
                <div class="grid-title">NARRATIVE INSIGHTS</div>
                <div v-if="!selectedClient">Select a client to view insights</div>
                <div v-else-if="isTempClient" class="tik-not-available">Not available for Te Kohinga People.</div>
                <div v-else-if="report">
                    <div class="report-header q-mb-md">
                        <div class="report-date text-body2 text-grey-6">
                            {{ formatDate(report.created_at) }}
                        </div>
                        <div class="text-body2">{{ report.title }}</div>
                        <q-btn
                            class="q-ml-md q-mb-lg"
                            label="View previous reports"
                            outline
                            color="primary"
                            no-caps
                            @click="previousReports = true"
                        />
                    </div>

                    <div class="summary-points">
                        <div v-for="(point, index) in summaryPoints" :key="index" class="summary-point q-mb-sm">
                            &bull; {{ point }}
                        </div>
                    </div>
                </div>
                <div v-else-if="loading" class="text-center">
                    <q-spinner color="primary" size="50px" class="q-mt-md" />
                </div>
                <div v-else class="text-center no-goals text-h6">
                    No reports. <router-link to="/report">Click here to go to report generation page.</router-link>
                </div>
            </div>

            <q-dialog v-model="previousReports">
                <NarrativeReports :clientId="selectedClient.client_id" @close="previousReports = false" />
            </q-dialog>

            <div class="grid-item goals-grid" :class="{ empty: !selectedClient }">
                <div class="grid-title">CLIENT GOALS</div>
                <div v-if="!selectedClient">Select a client to view goals</div>
                <div v-else-if="isTempClient" class="tik-not-available">Not available for Te Kohinga People.</div>
                <div v-else-if="loading" class="text-center">
                    <q-spinner color="primary" size="50px" class="q-mt-md" />
                </div>
                <div v-else>
                    <GoalTable :client_id="selectedClient.client_id" />
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import api from "@/services/api/api";
import notify from "@/services/util/notify";
import Radar from "@/components/ChartComponents/Radar.vue";
import NarrativeReports from "@/components/ReportComponents/NarrativeReports.vue";
import * as chartOptions from "@/components/DataComponents/Forms/formChartOptions";
import FormHistoryResponse from "@/components/DataComponents/Forms/FormHistoryResponseComponent.vue";
import GoalTable from "@/components/GoalComponents/GoalTable.vue";
import DashboardClientSummary from "./DashboardClientSummary.vue";
import { isTemporaryClientRow } from "@/services/util/formSubject";

export default {
    name: 'TeItiKahurangiDashboard',

    components: {
        DashboardClientSummary,
        FormHistoryResponse,
        GoalTable,
        NarrativeReports,
        Radar
    },

    props: {
        selectedClient: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            loading: false,
            chartData: {},
            overallData: null,
            radarOptions: chartOptions.retFormRadarOptions("Overall Results"),
            report: null,
            previousReports: false,
            showHistoricResponses: false
        };
    },

    computed: {
        // Temporary (tk_people) clients have no Paua client_id, so the legacy
        // TIK panels (spider/progress/narrative/goals) cannot render for them.
        isTempClient() {
            return isTemporaryClientRow(this.selectedClient);
        },

        totalSessions() {
            return this.overallData?.datasets?.length || this.selectedClient?.sessions_logged || 0;
        },

        daysSinceLastReport() {
            const datasets = this.overallData?.datasets;

            if (datasets?.length) {
                const latestDatasetLabel = datasets[datasets.length - 1].label.split('/');
                const latestTime = Date.parse(`${latestDatasetLabel[1]}/${latestDatasetLabel[0]}/${latestDatasetLabel[2]}`);
                return Math.floor((Date.now() - latestTime) / 86400000);
            }

            return this.getDaysSince(this.selectedClient?.latest_submitted_time);
        },

        sessionStats() {
            const categories = ['Ukaipo', 'Hinengaro', 'Tinana', 'Taiao', 'Rawa', 'Wairua'];
            const dataKeys = ['UkaipoData', 'HinengaroData', 'TinanaData', 'TaiaoData', 'RawaData', 'WairuaData'];

            return categories.map((category, index) => ({
                id: index + 1,
                statCategory: category,
                value: this.calculateStarChange(dataKeys[index])
            }));
        },

        summaryPoints() {
            if (!this.report?.summary) return [];

            if (Array.isArray(this.report.summary)) {
                return this.report.summary;
            }

            try {
                return JSON.parse(this.report.summary);
            } catch (error) {
                console.warn('Could not parse summary as JSON:', error);
                return [];
            }
        }
    },

    watch: {
        selectedClient: {
            immediate: true,
            handler() {
                this.resetClientData();

                if (this.selectedClient?.client_id) {
                    this.loadClientData();
                }
            }
        }
    },

    methods: {
        async loadClientData() {
            this.loading = true;

            try {
                const [formsResponse, reportResponse] = await Promise.all([
                    api.forms.getTeItiKahurangiFormDataCharts(this.selectedClient.client_id),
                    api.reports.previousNarrativeReport(this.selectedClient.client_id, 1)
                ]);

                this.chartData = formsResponse.data;
                this.overallData = formsResponse.data.OverAllData;
                this.report = reportResponse.data?.[0] || null;
            } catch (error) {
                notify.error("Failed to load client data");
            } finally {
                this.loading = false;
            }
        },

        resetClientData() {
            this.chartData = {};
            this.overallData = null;
            this.report = null;
        },

        calculateStarChange(dataKey) {
            const data = this.chartData[dataKey];
            if (!data?.datasets) return 0;

            let totalChange = 0;
            let count = 0;

            for (const dataset of data.datasets) {
                if (dataset.data.length >= 2) {
                    totalChange += dataset.data[dataset.data.length - 1] - dataset.data[dataset.data.length - 2];
                    count += 1;
                }
            }

            return count > 0 ? totalChange / count : 0;
        },

        formatDate(dateString) {
            if (!dateString) return 'N/A';
            return new Date(dateString).toLocaleString('en-nz');
        },

        getDaysSince(dateString) {
            if (!dateString) return 0;

            const timestamp = new Date(dateString).getTime();
            if (Number.isNaN(timestamp)) return 0;

            return Math.floor((Date.now() - timestamp) / 86400000);
        },

        onViewHistory() {
            // The legacy form-history dialog is Paua-only; internal clients have
            // no entries in the legacy TIK data store.
            if (this.isTempClient) {
                notify.warning("Form history view is not available for Te Kohinga People.", "top");
                return;
            }
            this.showHistoricResponses = true;
        }
    }
};
</script>

<style scoped>
@import "../../../assets/styles/clientDashboard.css";

.tik-not-available {
    padding: 24px 12px;
    color: #607175;
    text-align: center;
    font-size: 14px;
}
</style>
